import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { DashboardSummary, Metric } from "@/lib/api";
import SeverityBadge from "@/components/ui/SeverityBadge.tsx";
import RecommendedQtyCell from "@/components/ui/RecommendedQtyCell.tsx";
import OrderQtyBreakdownTooltip from "@/components/ui/OrderQtyBreakdownTooltip.tsx";
import RecentSpikesTooltip from "@/components/ui/RecentSpikesTooltip.tsx";
import { AlertCircle, AlertOctagon, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, ClipboardList, Clock, Filter, Loader2, Package, PackageOpen, RefreshCw, Search, ShieldAlert, ShoppingCart, TrendingDown } from "lucide-react";
import clsx from "clsx";

// Drill-down aus der Stammdaten-Abdeckung
const MISSING_FIELD_LABELS: Record<string, string> = {
  leadTime: 'Lieferzeit',
  moq: 'Mindestbestellmenge',
  packSize: 'Gebindegröße',
  supplier: 'Lieferant',
  purchasePrice: 'Einkaufspreis',
  erpMinStock: 'Mindestbestand (ERP)',
};

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatCurrency(amount: number | null) {
  if (amount === null) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
}

function isOrderOverdue(orderByDate: string | null): boolean {
  if (!orderByDate) return false;
  return orderByDate <= new Date().toISOString().slice(0, 10);
}

function worstComponentSeverity(components?: Metric['components']): 'red' | 'yellow' | 'green' | null {
  if (!components || components.length === 0) return null;
  const severities = components.map((c) => c.severity).filter(Boolean) as string[];
  if (severities.length === 0) return null;
  if (severities.includes('red')) return 'red';
  if (severities.includes('yellow')) return 'yellow';
  return 'green';
}

function criticalComponentNames(components?: Metric['components']): string {
  return (components || [])
    .filter((c) => c.severity === 'red' || c.severity === 'yellow')
    .map((c) => c.sku)
    .join(', ');
}

function getRowClass(isComponent = false) {
  if (isComponent) {
    return clsx(
      'cursor-pointer transition-colors border-b border-surface-3',
      'bg-surface-2 hover:bg-surface-3'
    );
  }

  return clsx(
    'cursor-pointer transition-colors border-b border-surface-3',
    'bg-surface hover:bg-surface-2'
  );
}

function getRiskCellClass(
  riskContributionMargin: number | null,
  usedAsComponent?: boolean
) {
  if (usedAsComponent) {
    return 'px-4 py-3 text-right tabular-nums font-mono text-[12.5px] text-ink-soft';
  }

  if (riskContributionMargin === null) {
    return 'px-4 py-3 text-right tabular-nums font-mono text-[12.5px] text-ink-soft';
  }

  if (riskContributionMargin >= 2500) {
    return 'px-4 py-3 text-right tabular-nums font-mono text-[12.5px] font-bold text-risk-red';
  }

  if (riskContributionMargin >= 1000) {
    return 'px-4 py-3 text-right tabular-nums font-mono text-[12.5px] font-semibold text-risk-red';
  }

  if (riskContributionMargin > 0) {
    return 'px-4 py-3 text-right tabular-nums font-mono text-[12.5px] font-semibold text-ink';
  }

  return 'px-4 py-3 text-right tabular-nums font-mono text-[12.5px] text-ink-soft';
}

function renderRiskValue(
  riskContributionMargin: number | null,
  options?: {
    usedAsComponent?: boolean;
    missingDataLabel?: string;
    severity?: 'red' | 'yellow' | 'green';
  }
) {
  if (options?.severity === 'green') return '—';
  if (options?.usedAsComponent) return 'Indirekt';
  if (riskContributionMargin === null) return options?.missingDataLabel ?? '—';
  if (riskContributionMargin <= 0) return '—';
  return formatCurrency(riskContributionMargin);
}

export default function OutOfStock() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const missingField = searchParams.get('missingField') ?? '';
  // Beim Drill-down aus der Stammdaten-Abdeckung interessieren alle betroffenen
  // Artikel, nicht nur die mit Risiko - sonst verschwindet der Grossteil sofort.
  const [severityFilter, setSeverityFilter] = useState<string>(missingField ? '' : 'risk');
  const [searchQuery, setSearchQuery] = useState('');
  // Verzögerter Suchwert – wird erst nach 300 ms Tipp-Pause aktualisiert
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    // Timer zurücksetzen, wenn sich searchQuery erneut ändert
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [expandedWarehouses, setExpandedWarehouses] = useState<Set<string>>(new Set());
  const [excludeSavingSku, setExcludeSavingSku] = useState<string | null>(null);
  const [excludeMessage, setExcludeMessage] = useState<string>('');
  const [pendingExcludedSkus, setPendingExcludedSkus] = useState<Set<string>>(new Set());

  // Purchase order selection
  const [selectedSkus, setSelectedSkus] = useState<Set<string>>(new Set());
  const [generating, setGenerating] = useState(false);

  const toggleWarehouses = (sku: string) => {
    setExpandedWarehouses((prev) => {
      const next = new Set(prev);
      next.has(sku) ? next.delete(sku) : next.add(sku);
      return next;
    });
  };

  const toggleRow = (sku: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(sku) ? next.delete(sku) : next.add(sku);
      return next;
    });
  };

  const loadData = async () => {
    try {
      const [s, m] = await Promise.all([
        api.getDashboardSummary(),
        api.getMetrics(missingField ? { missingField } : undefined),
        // api.getMetrics({
        //   ...(severityFilter && { severity: severityFilter }),
        //   ...(debouncedSearch.length >= 3 && { search: debouncedSearch }),
        // }),
      ]);
      setSummary(s);
      setMetrics(m);
      setPendingExcludedSkus(new Set());
    } catch (err) {
      console.error('Fehler beim Laden des Dashboards:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredMetrics = metrics.filter((m) => {
    const matchesSeverity =
      !severityFilter ||
      (severityFilter === 'risk'
        ? m.severity === 'red' || m.severity === 'yellow'
        : m.severity === severityFilter);

    const query = debouncedSearch.trim().toLowerCase();
    const matchesSearch =
      query.length < 3 ||
      m.sku.toLowerCase().includes(query) ||
      m.name.toLowerCase().includes(query);

    return matchesSeverity && matchesSearch;
  });

  // useEffect(() => {
  //   loadData();
  // }, [severityFilter, debouncedSearch]);
  useEffect(() => {
    loadData();
  }, [missingField]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.triggerSync();
      await loadData();
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setSyncing(false);
    }
  };


  const handleExcludeFromPlanning = async (sku: string, isExcludedFromPlanning: boolean) => {
    setExcludeSavingSku(sku);
    setExcludeMessage('');

    setPendingExcludedSkus((prev) => {
      const next = new Set(prev);
      if (isExcludedFromPlanning) {
        next.add(sku);
      } else {
        next.delete(sku);
      }
      return next;
    });

    try {
      await api.updateProductOverrides(sku, { isExcludedFromPlanning });
      setExcludeMessage('Artikel wurde erfolgreich aktualisiert.');
      await loadData();
    } catch (err) {
      console.error('Ausschluss fehlgeschlagen:', err);
      setExcludeMessage('Fehler beim Speichern. Bitte erneut versuchen.');
      setPendingExcludedSkus((prev) => {
        const next = new Set(prev);
        next.delete(sku);
        return next;
      });
    } finally {
      setExcludeSavingSku(null);
    }
  };

  const toggleSkuSelection = (sku: string, components?: Metric['components']) => {
    setSelectedSkus((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) {
        // Deselecting: leave components untouched per spec
        next.delete(sku);
      } else {
        next.add(sku);
        // Auto-select RED components of the main article
        components?.forEach((c) => {
          if (c.severity === 'red') {
            next.add(c.sku);
          }
        });
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allTopLevelSelected =
      filteredMetrics.length > 0 && filteredMetrics.every((m) => selectedSkus.has(m.sku));
    if (allTopLevelSelected) {
      setSelectedSkus(new Set());
    } else {
      setSelectedSkus(new Set(filteredMetrics.map((m) => m.sku)));
    }
  };

  // Distinguish top-level articles from components in the selection
  const topLevelSkuSet = new Set(metrics.map((m) => m.sku));
  const selectedArticleCount = Array.from(selectedSkus).filter((s) => topLevelSkuSet.has(s)).length;
  const selectedComponentCount = selectedSkus.size - selectedArticleCount;

  const handleGeneratePurchaseOrder = async () => {
    if (selectedSkus.size === 0) return;

    // Check for open purchase orders
    try {
      const { open } = await api.checkOpenPurchaseOrders();
      if (open.length > 0) {
        const openDate = new Date(open[0].createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
        if (!confirm(`Es existiert bereits eine offene Bestellliste vom ${openDate}. Trotzdem neue Liste erstellen?`)) {
          return;
        }
      }
    } catch {
      // Continue even if check fails
    }

    setGenerating(true);
    try {
      const order = await api.createPurchaseOrder(Array.from(selectedSkus));
      setSelectedSkus(new Set());
      navigate(`/bestelllisten/${order.id}`);
    } catch (err) {
      console.error('Bestellliste konnte nicht erstellt werden:', err);
      alert('Fehler beim Erstellen der Bestellliste.');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {missingField && (
        <div className="flex items-center gap-3 rounded-card border border-line bg-surface px-4 py-2.5">
          <span className="text-[13px] text-ink">
            Gefiltert auf Artikel ohne <span className="font-semibold">{MISSING_FIELD_LABELS[missingField] ?? missingField}</span>
          </span>
          <button
            onClick={() => setSearchParams({})}
            className="text-[12px] text-brand hover:underline"
          >
            Filter zurücksetzen
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <ShieldAlert className="h-8 w-8 text-brand" />
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-ink">Lagerrisiken</h1>
            <p className="text-sm text-ink-muted mt-1">
              {summary?.lastSyncAt ? (
                <>Letzter Sync: {formatDate(summary.lastSyncAt)} · Status: {summary.syncStatus}</>
              ) : (
                'Noch kein Sync durchgeführt'
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedSkus.size > 0 && (
            <button
              onClick={handleGeneratePurchaseOrder}
              disabled={generating}
              className="btn-primary flex items-center gap-2"
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardList className="h-4 w-4" />}
              Bestellliste generieren (
              {selectedArticleCount > 0 && `${selectedArticleCount} Artikel`}
              {selectedArticleCount > 0 && selectedComponentCount > 0 && ' + '}
              {selectedComponentCount > 0 && `${selectedComponentCount} Komponenten`}
              )
            </button>
          )}
          <button onClick={handleSync} disabled={syncing} className="btn-primary">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Sync & Berechnung
          </button>
        </div>
      </div>

      {/* Risk cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="rounded-card border border-line bg-surface flex items-center gap-4 p-5">
          <div className="flex-shrink-0 w-12 h-12 rounded-panel bg-risk-red-soft flex items-center justify-center">
            <AlertOctagon className="h-6 w-6 text-risk-red" />
          </div>
          <div>
            <p className="text-sm text-ink-muted">Gefährdeter Deckungsbeitrag</p>
            <p className="text-2xl font-extrabold tracking-tight text-risk-red">{formatCurrency(summary?.totalRiskContributionMargin ?? 0)}</p>
          </div>
        </div>

        <div className="rounded-card border border-line bg-surface flex items-center gap-4 p-5">
          <div className="flex-shrink-0 w-12 h-12 rounded-panel bg-risk-red-soft flex items-center justify-center">
            <AlertOctagon className="h-6 w-6 text-risk-red" />
          </div>
          <div>
            <p className="text-sm text-ink-muted">Gefährdeter Umsatz</p>
            <p className="text-2xl font-extrabold tracking-tight text-risk-red">{formatCurrency(summary?.totalRiskRevenue ?? 0)}</p>
          </div>
        </div>

        {(summary?.slowMoverCount ?? 0) > 0 && (
          <div
            className="rounded-card border border-line bg-surface flex items-center gap-4 p-5 cursor-pointer hover:bg-surface-2 transition-colors"
            onClick={() => navigate('/slow-movers')}
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-panel bg-risk-yellow-soft flex items-center justify-center">
              <Package className="h-6 w-6 text-risk-yellow" />
            </div>
            <div>
              <p className="text-sm text-ink-muted">Kapitalbindung Überbestand</p>
              <p className="text-2xl font-extrabold tracking-tight text-risk-yellow">{formatCurrency(summary?.slowMoverKapitalbindung ?? 0)}</p>
              <p className="text-xs text-ink-soft">{summary?.slowMoverCount} Artikel</p>
            </div>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          className="rounded-card border border-line bg-surface flex items-center gap-4 p-5 cursor-pointer hover:bg-surface-2 transition-colors"
          onClick={() => setSeverityFilter('red')}
        >
          <div className="flex-shrink-0 w-12 h-12 rounded-panel bg-risk-red-soft flex items-center justify-center">
            <AlertCircle className="h-6 w-6 text-risk-red" />
          </div>
          <div>
            <p className="text-sm text-ink-muted">Kritisch</p>
            <p className="text-2xl font-extrabold tracking-tight text-risk-red">{summary?.red ?? 0}</p>
          </div>
        </div>

        <div
          className="rounded-card border border-line bg-surface flex items-center gap-4 p-5 cursor-pointer hover:bg-surface-2 transition-colors"
          onClick={() => setSeverityFilter('yellow')}
        >
          <div className="flex-shrink-0 w-12 h-12 rounded-panel bg-risk-yellow-soft flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-risk-yellow" />
          </div>
          <div>
            <p className="text-sm text-ink-muted">Warnung</p>
            <p className="text-2xl font-extrabold tracking-tight text-risk-yellow">{summary?.yellow ?? 0}</p>
          </div>
        </div>

        <div
          className="rounded-card border border-line bg-surface flex items-center gap-4 p-5 cursor-pointer hover:bg-surface-2 transition-colors"
          onClick={() => setSeverityFilter('green')}
        >
          <div className="flex-shrink-0 w-12 h-12 rounded-panel bg-brand-soft flex items-center justify-center">
            <CheckCircle2 className="h-6 w-6 text-brand" />
          </div>
          <div>
            <p className="text-sm text-ink-muted">OK</p>
            <p className="text-2xl font-extrabold tracking-tight text-brand">{summary?.green ?? 0}</p>
          </div>
        </div>

        <div className="rounded-card border border-line bg-surface flex items-center gap-4 p-5">
          <div className="flex-shrink-0 w-12 h-12 rounded-panel bg-brand-soft flex items-center justify-center">
            <Clock className="h-6 w-6 text-brand" />
          </div>
          <div>
            <p className="text-sm text-ink-muted">Sync Status</p>
            <p className="text-sm font-semibold text-ink">
              {summary?.syncStatus === 'success' ? 'Erfolgreich' : summary?.syncStatus === 'error' ? 'Fehler' : 'Ausstehend'}
            </p>
          </div>
        </div>
      </div>

      {/* Next OOS Top 5 */}
      {summary?.nextOosTop5 && summary.nextOosTop5.length > 0 && (
        <div className="rounded-card border border-line bg-surface p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingDown className="h-5 w-5 text-risk-red" />
            <h2 className="text-[16.5px] font-extrabold tracking-tight text-ink">Nächste Out-of-Stock Risiken</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {summary.nextOosTop5.map((item) => (
              <div
                key={item.sku}
                onClick={() => navigate(`/products/${encodeURIComponent(item.sku)}`)}
                className="flex items-center gap-3 p-3 rounded-panel border border-line bg-surface-2 hover:border-brand-soft-border hover:bg-brand-soft/30 cursor-pointer transition-colors"
              >
                <div
                  className={clsx(
                    'w-2 h-8 rounded-full flex-shrink-0',
                    item.severity === 'red' ? 'bg-risk-red' : 'bg-risk-yellow'
                  )}
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-1 min-w-0">
                    <p className="text-[13.5px] font-bold text-ink truncate">{item.name}</p>
                    {item.usedAsComponent && (
                      <span className="font-mono text-[10px] text-ink-soft bg-surface border border-line rounded px-1.5 py-0.5 flex-shrink-0">
                        Komponente
                      </span>
                    )}
                  </div>
                  <p className="font-mono text-[10.5px] text-ink-soft">{item.coverageDays.toFixed(0)} {item.coverageDays <= 2 ? 'Tag' : 'Tage'} Reichweite</p>
                  <p className="font-mono text-[10.5px] text-ink-soft">OOS ab {formatDate(item.oosDate)}</p>

                  {item.topLevel && item.riskContributionMargin !== null && item.riskContributionMargin > 0 ? (
                    <p className="font-mono text-[10.5px] text-ink-soft">DB-Risiko {formatCurrency(item.riskContributionMargin)}</p>
                  ) : item.usedAsComponent ? (
                    <p className="font-mono text-[10.5px] text-ink-soft">indirektes Risiko über Fertigartikel</p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Order Now Table */}
      <div className="rounded-card border border-line bg-surface overflow-hidden">
        <div className="px-5 py-4 border-b border-line flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-brand" />
              <h2 className="text-[16.5px] font-extrabold tracking-tight text-ink">Bestellübersicht</h2>
            </div>
            <p className="font-mono text-[10.5px] text-ink-soft mt-0.5">
              Ausgeschlossene Artikel werden im Dashboard, in KPIs, OOS Risiken, Alerts und in der Bestellplanung nicht berücksichtigt.
            </p>
            {excludeMessage && <p className="text-xs mt-1 text-ink-muted">{excludeMessage}</p>}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-soft" />
              <input
                type="text"
                placeholder="SKU oder Name suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-field pl-9 w-48"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-soft" />
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                className="input-field pl-9 pr-8 appearance-none"
              >
                <option value="">Alle</option>
                <option value="risk">Kritisch + Warnung</option>
                <option value="red">Kritisch</option>
                <option value="yellow">Warnung</option>
                <option value="green">OK</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-3 text-left">
                <th className="px-3 py-2.5 text-center w-10">
                  <input
                    type="checkbox"
                    checked={filteredMetrics.length > 0 && selectedSkus.size === filteredMetrics.length}
                    onChange={toggleSelectAll}
                    className="cursor-pointer"
                  />
                </th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3">SKU</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3">Produkt</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3">Status</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3">Produktion</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3 text-right">DB-Risiko</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3 text-right">Reichweite</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3 text-right">OOS Datum</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3 text-right">Bestellen bis</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3 text-right">Versandbestand</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3 text-right">Empf. Menge</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3 text-right">Tägl. Verbrauch</th>
                <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-ink-soft border-b border-surface-3 text-center">Ausschließen</th>
              </tr>
            </thead>
            <tbody>
              {filteredMetrics.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-4 py-12 text-center text-ink-soft">
                    Keine Daten vorhanden. Führen Sie einen Sync durch.
                  </td>
                </tr>
              ) : (
                filteredMetrics.map((metric) => (
                  <React.Fragment key={metric.sku}>
                    <tr
                      key={metric.sku}
                      onClick={() => navigate(`/products/${encodeURIComponent(metric.sku)}`)}
                      className={getRowClass()}
                    >
                      <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedSkus.has(metric.sku)}
                          onChange={() => toggleSkuSelection(metric.sku, metric.components)}
                          className="cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 font-mono text-[10.5px] text-ink-soft">
                        <div className="flex items-center gap-1">
                          {/* Expand-Button für Artikel mit Komponenten oder Verkaufseinheiten */}
                          {((metric.components && metric.components.length > 0) || (metric.packagingUnits && metric.packagingUnits.length > 0)) && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleRow(metric.sku); }}
                              className="text-ink-soft hover:text-ink transition-colors"
                            >
                              {expandedRows.has(metric.sku)
                                ? <ChevronDown className="h-3.5 w-3.5" />
                                : <ChevronRight className="h-3.5 w-3.5" />}
                            </button>
                          )}
                          {metric.sku}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[13.5px] font-bold text-ink">{metric.name}</td>
                      <td className="px-4 py-3"><SeverityBadge severity={metric.severity} /></td>
                      <td className="px-4 py-3">
                        {(() => {
                          const prodSeverity = worstComponentSeverity(metric.components);
                          if (!prodSeverity) return <span className="text-ink-soft font-mono text-[12.5px]">—</span>;
                          if (prodSeverity === 'green') return <SeverityBadge severity="green" />;
                          return (
                            <span title={`Kritische Komponenten: ${criticalComponentNames(metric.components)}`} className="cursor-help">
                              <SeverityBadge severity={prodSeverity} />
                            </span>
                          );
                        })()}
                      </td>
                      <td className={getRiskCellClass(metric.riskContributionMargin, metric.usedAsComponent)}>
                        {renderRiskValue(metric.riskContributionMargin, {
                          usedAsComponent: metric.usedAsComponent,
                          missingDataLabel: 'Keine DB-Daten',
                          severity: metric.severity,
                        })}
                      </td>
                      {/*<td className="px-4 py-3 text-right tabular-nums font-semibold">{m.riskContributionMargin === null || m.riskContributionMargin > 0 ? formatCurrency(m.riskContributionMargin) : '—'}</td>*/}
                      <td className="px-4 py-3 text-right tabular-nums font-mono text-[12.5px] text-ink-muted">{metric.coverageDays.toFixed(0)} Tage</td>
                      <td className="px-4 py-3 text-right tabular-nums font-mono text-[12.5px] text-ink-muted">{formatDate(metric.oosDate)}</td>
                      <td className={clsx(
                        'px-4 py-3 text-right tabular-nums font-mono text-[12.5px]',
                        isOrderOverdue(metric.orderByDate) ? 'text-risk-red font-semibold' : 'text-ink-muted'
                      )}>
                        <span className="inline-flex items-center gap-1.5 justify-end" title={isOrderOverdue(metric.orderByDate) ? 'Spätester Bestellzeitpunkt erreicht - heute bestellen' : undefined}>
                          {isOrderOverdue(metric.orderByDate) && <AlertTriangle className="h-3.5 w-3.5" />}
                          {formatDate(metric.orderByDate)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-mono text-[12.5px] text-ink-muted">
                        <span className="inline-flex items-center gap-1.5 justify-end">
                          {metric.warehouseBreakdown && metric.warehouseBreakdown.length > 0 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleWarehouses(metric.sku); }}
                              className="text-ink-soft hover:text-ink transition-colors"
                              aria-label={expandedWarehouses.has(metric.sku) ? 'Lager einklappen' : 'Lager ausklappen'}
                              title="Alle Lagerbestände anzeigen; nur Versandlager zählen für die Reichweite"
                            >
                              {expandedWarehouses.has(metric.sku)
                                ? <ChevronDown className="h-3.5 w-3.5" />
                                : <ChevronRight className="h-3.5 w-3.5" />}
                            </button>
                          )}
                          {metric.availableQty.toLocaleString('de-DE')}
                          {metric.minStockBreached && (
                            <span
                              className="inline-flex items-center rounded-full border border-risk-yellow/40 bg-risk-yellow/10 px-1.5 py-0.5 text-[9px] font-sans font-medium text-risk-yellow"
                              title="Bestand unter weclapp-Mindestbestand"
                            >
                              Min
                            </span>
                          )}
                        </span>
                        {metric.onHand !== metric.availableQty && (
                          <div
                            className="text-[10px] text-ink-soft"
                            title="Physischer Versandbestand vor Abzug der Reservierungen. Weitere Lager werden separat angezeigt."
                          >
                            physisch {metric.onHand.toLocaleString('de-DE')}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-mono text-[12.5px] font-semibold text-ink">
                        <span className="inline-flex items-center gap-1.5 justify-end">
                          {metric.recommendationBlockedByOverduePo ? (
                            <span
                              title="Empfehlung 0, weil bestätigter Zulauf überfällig ist. Bestellung in weclapp prüfen: nachfassen oder manuell schließen."
                              className="inline-flex items-center gap-1 font-mono text-[10px] font-semibold text-risk-yellow bg-risk-yellow-soft border border-risk-yellow/30 rounded px-1.5 py-0.5 cursor-help"
                            >
                              <AlertTriangle className="h-3 w-3" />
                              Zulauf überfällig
                            </span>
                          ) : (
                            <RecommendedQtyCell qty={metric.recommendedOrderQty} severity={metric.severity} />
                          )}
                          {metric.orderQtyBreakdown && metric.orderQtyBreakdown.length > 0 && (
                            <OrderQtyBreakdownTooltip
                              total={metric.recommendedOrderQty}
                              breakdown={metric.orderQtyBreakdown}
                            />
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-mono text-[12.5px] text-ink-muted">
                        <span className="inline-flex items-center gap-1.5 justify-end">
                          {metric.dailyUsage.toFixed(2)}
                          {metric.recentSpikes && metric.recentSpikes.length > 0 && (
                            <RecentSpikesTooltip spikes={metric.recentSpikes} />
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <label className="inline-flex items-center gap-2 text-xs text-ink-muted" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={pendingExcludedSkus.has(metric.sku)}
                            disabled={excludeSavingSku === metric.sku}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => handleExcludeFromPlanning(metric.sku, e.target.checked)}
                          />
                          {excludeSavingSku === metric.sku ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                        </label>
                      </td>
                    </tr>

                    {/* Ausgeklappte Produktionskomponenten */}
                    {/* All physical warehouse stocks stay visible independently of shipping coverage. */}
                    {expandedWarehouses.has(metric.sku) && metric.warehouseBreakdown && metric.warehouseBreakdown.map((wh, idx) => (
                      <tr
                        key={`${metric.sku}-wh-${wh.warehouseId ?? 'unassigned'}-${idx}`}
                        className="bg-surface-2 border-b border-surface-3"
                      >
                        <td colSpan={9} className="px-4 py-2 text-right font-mono text-[10.5px] text-ink-soft">
                          {wh.warehouseName === null
                            ? <span className="italic">Nicht zugeordnet</span>
                            : wh.warehouseName}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums font-mono text-[12.5px] text-ink-muted">
                          {wh.onHand.toLocaleString('de-DE')}
                        </td>
                        <td colSpan={3} className="px-4 py-2 text-[10.5px] text-ink-soft">
                          {wh.shippingWarehouse ? 'Versandlager · zählt für Reichweite' : 'Weiteres Lager · zählt nicht für Reichweite'}
                        </td>
                      </tr>
                    ))}

                    {expandedRows.has(metric.sku) && metric.components && metric.components.map((component) => {
                      const hasData = component.severity != null;

                      return (
                        <tr
                          key={`${metric.sku}-${component.sku}`}
                          onClick={() => navigate(`/products/${encodeURIComponent(component.sku)}`)}
                          className="bg-surface-2 hover:bg-surface-3 cursor-pointer transition-colors border-b border-surface-3"
                        >
                          <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                            {hasData && (
                              <input
                                type="checkbox"
                                checked={selectedSkus.has(component.sku)}
                                onChange={() => toggleSkuSelection(component.sku)}
                                className="cursor-pointer scale-90"
                              />
                            )}
                          </td>
                          <td className="px-4 py-2 font-mono text-[10.5px] text-ink-soft">
                            <div className="flex items-center gap-2 pl-5">
                              <Package className="h-3 w-3 text-ink-soft flex-shrink-0" />
                              {component.sku}
                            </div>
                          </td>

                          {hasData ? (
                            <>
                              <td className="px-4 py-2 text-[13.5px] font-bold text-ink">{component.name}</td>
                              <td className="px-4 py-2"><SeverityBadge severity={component.severity!} /></td>
                              <td className="px-4 py-2"></td>
                              <td className="px-4 py-3 text-right font-mono text-[12.5px] font-semibold tabular-nums text-ink">{component.riskContributionMargin === null || component.riskContributionMargin > 0 ? formatCurrency(component.riskContributionMargin) : '—'}</td>
                              <td className="px-4 py-2 text-right tabular-nums font-mono text-[10.5px] text-ink-soft">{component.coverageDays!.toFixed(0)} Tage</td>
                              <td className="px-4 py-2 text-right tabular-nums font-mono text-[10.5px] text-ink-soft">{formatDate(component.oosDate)}</td>
                              <td className={clsx(
                                'px-4 py-2 text-right tabular-nums font-mono text-[10.5px]',
                                isOrderOverdue(component.orderByDate) ? 'text-risk-red font-semibold' : 'text-ink-soft'
                              )}>{formatDate(component.orderByDate)}</td>
                              <td className="px-4 py-2 text-right tabular-nums font-mono text-[10.5px] text-ink-soft">{component.availableQty!.toLocaleString('de-DE')}</td>
                              <td className="px-4 py-2 text-right tabular-nums font-mono text-[10.5px] text-ink-soft font-semibold">
                                <span className="inline-flex items-center gap-1.5 justify-end">
                                  <RecommendedQtyCell qty={component.recommendedOrderQty!} severity={component.severity!} />
                                  {component.orderQtyBreakdown && component.orderQtyBreakdown.length > 0 && (
                                    <OrderQtyBreakdownTooltip
                                      total={component.recommendedOrderQty!}
                                      breakdown={component.orderQtyBreakdown}
                                    />
                                  )}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-right tabular-nums font-mono text-[10.5px] text-ink-soft">
                                <span className="inline-flex items-center gap-1.5 justify-end">
                                  {component.dailyUsage!.toFixed(2)}
                                  {component.recentSpikes && component.recentSpikes.length > 0 && (
                                    <RecentSpikesTooltip spikes={component.recentSpikes} />
                                  )}
                                </span>
                              </td>
                              <td className="px-4 py-2"></td>
                            </>
                          ) : (
                            <td colSpan={10} className="px-4 py-2 font-mono text-[10.5px] text-ink-soft italic">
                              Produktionskomponente
                            </td>
                          )}
                        </tr>
                      );
                    })}

                    {/* Ausgeklappte Verkaufseinheiten (VE) */}
                    {expandedRows.has(metric.sku) && metric.packagingUnits && metric.packagingUnits.map((packagingUnit) => (
                      <tr
                        key={`${metric.sku}-ve-${packagingUnit.sku}`}
                        className="bg-brand-soft/20 border-b border-surface-3 transition-colors"
                      >
                        <td className="px-3 py-2"></td>
                        <td className="px-4 py-2 font-mono text-[10.5px] text-brand-deep">
                          <div className="flex items-center gap-2 pl-5">
                            <PackageOpen className="h-3 w-3 text-brand flex-shrink-0" />
                            {packagingUnit.sku}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-[12.5px] text-ink-muted">
                          {packagingUnit.name ?? packagingUnit.sku}
                          <span className="ml-2 font-mono text-[10px] text-brand-deep bg-brand-soft border border-brand-soft-border rounded px-1.5 py-0.5">
                            VE ×{packagingUnit.quantity}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          {/* VE haben keine eigene Severity */}
                        </td>
                        <td colSpan={7} className="px-4 py-2 font-mono text-[10.5px] text-ink-soft tabular-nums">
                          Ø {packagingUnit.avgDailyUsageVe.toFixed(2)} VE/Tag → {packagingUnit.avgDailyUsageBase.toFixed(2)} Stk./Tag
                        </td>
                        <td className="px-4 py-2"></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
